import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCertificate, faSpinner, faLink, faCheckCircle, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import api from '../../../Api';

const AttestationTab = ({ projectId, project, readOnly }) => {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [toggleId, setToggleId] = useState(null);
  const [rowErrors, setRowErrors] = useState({}); // key -> message

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [slotsRes, checklistRes] = await Promise.all([
        api.get(`/projects/${projectId}/slots`),
        api.get(`/projects/${projectId}/checklists`, { params: { include_slot_items: 1 } }),
      ]);
      const data = slotsRes.data.data || {};
      const checklistItems = checklistRes.data.data || [];

      // Map: slotId or 'project' → { received: bool, itemId: int }
      const itemMap = {};
      checklistItems.forEach(c => {
        if (c.step === 'closing' && c.label === 'Attestation de référence') {
          const k = c.survey_slot_id ?? 'project';
          itemMap[k] = { received: !!c.is_completed, itemId: c.id };
        }
      });

      const allSlots = [
        ...(data.waves || []).flatMap(w => (w.survey_slots || []).map(s => ({ ...s, waveName: w.name }))),
        ...(data.standalone_slots || []).map(s => ({ ...s, waveName: null })),
      ];
      const closingSlots = allSlots.filter(s => s.step === 'closing');

      let list = closingSlots.map(s => ({
        type: 'slot', id: s.id, name: s.name,
        survey: s.survey,
        linked_surveys: s.linked_surveys || [],
        attestation_requested: s.attestation_requested ?? null,
        attestation_request_sent_at: s.attestation_request_sent_at || null,
        attestation_relance_count: s.attestation_relance_count || 0,
        attestation_received: !!itemMap[s.id]?.received,
        attestation_item_id: itemMap[s.id]?.itemId || null,
      }));

      if (list.length === 0 && project?.step === 'closing') {
        list = [{
          type: 'project', id: project.id, name: project.name, survey: null,
          attestation_requested: project.attestation_requested ?? null,
          attestation_request_sent_at: project.attestation_request_sent_at || null,
          attestation_relance_count: project.attestation_relance_count || 0,
          attestation_received: !!itemMap['project']?.received,
          attestation_item_id: itemMap['project']?.itemId || null,
        }];
      }

      setEntities(list);
    } catch { setEntities([]); }
    finally { setLoading(false); }
  }, [projectId, project]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setAttestation = async (entity, requested) => {
    const key = `${entity.type}-${entity.id}`;
    setSavingId(key);
    try {
      const url = entity.type === 'slot'
        ? `/projects/${projectId}/slots/${entity.id}/attestation`
        : `/projects/${projectId}/attestation`;
      await api.post(url, { requested });
      // Re-fetch so we get the new checklist item id when requested=true
      fetchData();
    } catch { }
    finally { setSavingId(null); }
  };

  const sendAttestation = async (entity) => {
    const key = `${entity.type}-${entity.id}`;
    setSendingId(key);
    setRowErrors(prev => ({ ...prev, [key]: null }));
    try {
      const url = entity.type === 'slot'
        ? `/projects/${projectId}/slots/${entity.id}/attestation-request`
        : `/projects/${projectId}/attestation-request`;
      const res = await api.post(url);
      setEntities(prev => prev.map(e => (e.type === entity.type && e.id === entity.id) ? {
        ...e,
        attestation_request_sent_at: res.data.sent_at,
        attestation_relance_count: res.data.relance_count ?? e.attestation_relance_count,
      } : e));
    } catch (err) {
      setRowErrors(prev => ({ ...prev, [key]: err.response?.data?.message || 'Erreur lors de l\'envoi' }));
    }
    finally { setSendingId(null); }
  };

  const toggleReceived = async (entity) => {
    if (!entity.attestation_item_id) return;
    const key = `${entity.type}-${entity.id}`;
    setToggleId(key);
    try {
      await api.post(`/projects/${projectId}/checklists/${entity.attestation_item_id}/toggle`);
      setEntities(prev => prev.map(e => (e.type === entity.type && e.id === entity.id) ? { ...e, attestation_received: !e.attestation_received } : e));
    } catch { }
    finally { setToggleId(null); }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600" /></div>;
  }

  if (entities.length === 0) {
    return (
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '40px 20px', textAlign: 'center' }}>
        <FontAwesomeIcon icon={faCertificate} style={{ fontSize: '1.8rem', color: '#e5e7eb', marginBottom: 12 }} />
        <div style={{ fontSize: '0.85rem', color: '#9e9e9e' }}>
          Disponible une fois le projet ou ses enquêtes en étape Closing
        </div>
      </div>
    );
  }

  const statusLabel = (e) => {
    if (e.attestation_received) return 'Reçue';
    if (e.attestation_relance_count > 0) return 'Relancé';
    if (e.attestation_request_sent_at) return 'Demandé';
    return 'À demander';
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
        <FontAwesomeIcon icon={faCertificate} size="xs" /> Attestation de référence
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {entities.map(entity => {
          const key = `${entity.type}-${entity.id}`;
          const saving = savingId === key;
          const sending = sendingId === key;
          const toggling = toggleId === key;
          const att = entity.attestation_requested;
          const sentAt = entity.attestation_request_sent_at;
          const sentToday = sentAt && new Date(sentAt).toDateString() === new Date().toDateString();
          const sendLabel = sending
            ? 'Envoi…'
            : sentToday
              ? "Envoyée aujourd'hui"
              : (sentAt ? 'Envoyer relance' : 'Envoyer demande');
          const status = statusLabel(entity);
          const statusColor = entity.attestation_received ? '#166534' : (sentAt ? '#9a3412' : '#9a3412');

          return (
            <div key={key} style={{ background: '#f9fafb', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.84rem', fontWeight: 600, color: '#111827' }}>{entity.name}</div>
                  {/* Every survey the slot covers — an attestation can span several. */}
                  {(entity.linked_surveys?.length || 0) > 1 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {entity.linked_surveys.map(l => (
                        <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.68rem', color: '#6b7280' }}>
                          <FontAwesomeIcon icon={faLink} size="xs" style={{ color: '#d1d5db' }} />
                          {l.survey?.title} ({l.survey?.limesurvey_id})
                          {l.survey_wave && <span style={{ color: '#7c3aed' }}>· {l.survey_wave.label}</span>}
                        </div>
                      ))}
                    </div>
                  ) : entity.survey && (
                    <div style={{ fontSize: '0.68rem', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                      <FontAwesomeIcon icon={faLink} size="xs" style={{ color: '#d1d5db' }} />{entity.survey.title} ({entity.survey.limesurvey_id})
                    </div>
                  )}
                </div>

                {readOnly ? (
                  <span style={{ fontSize: '0.72rem', color: statusColor, fontWeight: entity.attestation_received ? 600 : 500 }}>
                    {att === true ? status : att === false ? 'Non demandée' : 'Non décidée'}
                  </span>
                ) : att === null ? (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => setAttestation(entity, true)} disabled={saving}
                      style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: '#ea580c', color: '#fff', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.5 : 1 }}>
                      {saving ? <FontAwesomeIcon icon={faSpinner} spin size="xs" /> : 'Demander'}
                    </button>
                    <button onClick={() => setAttestation(entity, false)} disabled={saving}
                      style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>
                      Non
                    </button>
                  </div>
                ) : att === true ? (
                  <span style={{ fontSize: '0.72rem', fontWeight: 600, color: statusColor, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FontAwesomeIcon icon={faCheckCircle} size="xs" /> {status}
                  </span>
                ) : (
                  <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>Non demandée</span>
                )}
              </div>

              {/* Send/Relance + Received toggle row — only when requested and editable */}
              {!readOnly && att === true && !entity.attestation_received && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #e5e7eb' }}>
                  {rowErrors[key] && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: '0.7rem', padding: '6px 8px', borderRadius: 6, marginBottom: 8 }}>{rowErrors[key]}</div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {/* Received checkbox */}
                    {entity.attestation_item_id && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: '#6b7280', cursor: toggling ? 'default' : 'pointer' }}>
                        <input type="checkbox" checked={entity.attestation_received} onChange={() => toggleReceived(entity)} disabled={toggling}
                          style={{ accentColor: '#dc2626' }} />
                        Marquer comme reçue
                      </label>
                    )}
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                      {sentAt && !sentToday && (
                        <span style={{ fontSize: '0.66rem', color: '#9a3412' }}>
                          Dernier envoi : {new Date(sentAt).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                      <button onClick={() => sendAttestation(entity)} disabled={sending || sentToday}
                        style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: sentToday ? '#e5e7eb' : '#ea580c', color: sentToday ? '#9ca3af' : '#fff', fontSize: '0.72rem', fontWeight: 600, cursor: (sending || sentToday) ? 'default' : 'pointer', opacity: sending ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 5 }}>
                        <FontAwesomeIcon icon={faEnvelope} size="xs" /> {sendLabel}
                      </button>
                      <button onClick={() => setAttestation(entity, false)} disabled={saving}
                        style={{ fontSize: '0.7rem', padding: '5px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', cursor: 'pointer' }}>
                        Annuler
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Received row — when received, allow unchecking */}
              {!readOnly && att === true && entity.attestation_received && entity.attestation_item_id && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: '#166534', fontWeight: 500, cursor: toggling ? 'default' : 'pointer' }}>
                    <input type="checkbox" checked={true} onChange={() => toggleReceived(entity)} disabled={toggling}
                      style={{ accentColor: '#166534' }} />
                    Reçue
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: 14, lineHeight: 1.5 }}>
        Une fois la demande activée, un email peut être envoyé depuis ici. Cochez « Marquer comme reçue » lorsque le client l'a fournie.
      </div>
    </div>
  );
};

export default AttestationTab;
